import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  cloneWorkflowDefinition,
  createWorkflowRun,
  getWorkflowRuntimeStatus,
  isValidWorkflowDefinitionPublicId,
  isValidWorkflowRunPublicId,
  listActiveWorkflowRuns,
  listWorkflowDefinitions,
  normalizeWorkflowRun,
  preflightWorkflowDefinition,
  workflowDefinitionPublicId,
  type WorkflowDefinition,
  type WorkflowPreflightResult,
  type WorkflowRun,
  type WorkflowRuntimeStatus,
} from '../api/workflowRuns'
import { ChromePrimaryButton, ChromeTextInput, ChromeToolbarButton } from '../components/ChromeActions'
import { ChromeTooltip } from '../components/ChromeTooltip'
import { TimelinePullIndicator } from '../components/create/TimelineRefreshSurfaces'
import { ThemeModal } from '../components/modal'
import { ThemeReveal } from '../components/motion/ThemeMotion'
import { SelectMenu } from '../components/SelectMenu'
import { WorkflowActiveRunsPanel } from '../components/workflow/WorkflowActiveRunsPanel'
import { WorkflowSystemTemplateSection } from '../components/workflow/WorkflowSystemTemplateSection'
import { WorkflowShelf } from '../components/workflow/WorkflowShelf'
import { ThemedWorkflowState } from '../components/workflow/ThemedWorkflowState'
import { WorkflowFormField } from '../components/workflow/WorkflowFormLayout'
import { WorkflowPreflightPanel } from '../components/workflow/WorkflowPreflightPanel'
import { WorkflowRunInputFields } from '../components/workflow/WorkflowRunInputFields'
import { usePlatformConfig } from '../context/PlatformConfigContext'
import { useToast } from '../context/ToastContext'
import { USER_PERMISSION, useUserPerm } from '../hooks/useUserPerm'
import { useScrollPullToRefresh } from '../hooks/useScrollPullToRefresh'
import { ContentSkeleton } from '../layouts/ContentSkeleton'
import { showApiErrorToast } from '../lib/apiErrorToast'
import { buildLocalWorkflowPreflight } from '../lib/workflow/workflowLocalPreflight'
import { resolveWorkflowDefinitionDisplay } from '../lib/workflow/workflowDefinitionDisplay'
import { buildWorkflowsPageBreadcrumbs } from '../lib/workflowBreadcrumbs'
import { isWorkflowRunActive } from '../lib/workflow/workflowRunDisplay'
import { navigateToSimulatedWorkflowRun, workflowRunPagePath } from '../lib/workflow/workflowRunRoute'
import { workflowRunHub } from '../lib/workflowRunEventStream'
import {
  buildRunInputPayload,
  defaultInputValues,
  inferWorkflowInputFields,
} from '../lib/workflow/workflowInputFields'
import {
  hydrateComicProjectInitInputFields,
  isComicProjectInitDefinition,
  normalizeComicProjectInitInputValues,
} from '../lib/workflow/workflowComicProjectInit'
import {
  WORKFLOW_TYPE_META,
  type WorkflowType,
  workflowTypeOptions,
} from '../lib/workflow/workflowTemplates'
import { WORKFLOWS_PAGE_COPY as copy } from '../lib/workflow/workflowsPageCopy'
import { useTheme } from '../theme/ThemeContext'

function createBuilderUrl(type: WorkflowType, name: string, description: string) {
  const params = new URLSearchParams({ type, name: name.trim() })
  const remark = description.trim()
  if (remark) params.set('description', remark)
  return `/workflows/builder/new?${params.toString()}`
}

function buildWorkflowRunForwardSearch(search: string): string {
  const params = new URLSearchParams(search)
  const next = new URLSearchParams()
  for (const key of ['applyProjectId', 'applyMode', 'targetEpisodeId']) {
    const value = params.get(key)?.trim()
    if (value) next.set(key, value)
  }
  const suffix = next.toString()
  return suffix ? `?${suffix}` : ''
}

export function WorkflowsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme } = useTheme()
  const { showToast } = useToast()
  const angular = theme === 'pixel' || theme === 'geek'
  const canRun = useUserPerm(USER_PERMISSION.workflowRunCreate)
  const { clientUi, remoteSynced: platformCatalogReady } = usePlatformConfig()

  const [definitions, setDefinitions] = useState<WorkflowDefinition[]>([])
  const [systemTemplates, setSystemTemplates] = useState<WorkflowDefinition[]>([])
  const [activeRuns, setActiveRuns] = useState<WorkflowRun[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [runningDefinitionId, setRunningDefinitionId] = useState<string | null>(null)
  const [cloningDefinitionId, setCloningDefinitionId] = useState<string | null>(null)
  const [runTarget, setRunTarget] = useState<WorkflowDefinition | null>(null)
  const [runInputValues, setRunInputValues] = useState<Record<string, string>>({})
  const [createTypeOpen, setCreateTypeOpen] = useState(false)
  const [createType, setCreateType] = useState<WorkflowType>('comic_story')
  const [createName, setCreateName] = useState(WORKFLOW_TYPE_META.comic_story.defaultName)
  const [createDescription, setCreateDescription] = useState('')
  const [runtimeStatus, setRuntimeStatus] = useState<WorkflowRuntimeStatus | null>(null)
  const [preflightResult, setPreflightResult] = useState<WorkflowPreflightResult | null>(null)
  const [preflightLoading, setPreflightLoading] = useState(false)
  const workflowScrollRef = useRef<HTMLDivElement | null>(null)
  const [workflowPullAttachSignal, setWorkflowPullAttachSignal] = useState(0)
  const hasInitializedRef = useRef(false)
  const workflowPullRefreshLockRef = useRef(false)
  const openedTemplateSearchRef = useRef<string | null>(null)

  const baseRunInputFields = useMemo(
    () => (runTarget ? inferWorkflowInputFields(runTarget.graph) : []),
    [runTarget],
  )
  const runInputFields = useMemo(
    () =>
      runTarget && isComicProjectInitDefinition(runTarget) && platformCatalogReady
        ? hydrateComicProjectInitInputFields(baseRunInputFields, clientUi, runInputValues)
        : baseRunInputFields,
    [baseRunInputFields, clientUi, platformCatalogReady, runInputValues, runTarget],
  )

  const definitionNames = useMemo(() => {
    const names: Record<string, string> = {}
    for (const item of [...definitions, ...systemTemplates]) {
      const id = workflowDefinitionPublicId(item)
      if (isValidWorkflowDefinitionPublicId(id)) {
        names[id] = resolveWorkflowDefinitionDisplay(item).name
      }
    }
    return names
  }, [definitions, systemTemplates])

  const setWorkflowScrollPort = useCallback((node: HTMLDivElement | null) => {
    workflowScrollRef.current = node
    setWorkflowPullAttachSignal((n) => n + 1)
  }, [])

  const refreshActiveRuns = useCallback(async () => {
    if (!canRun) {
      setActiveRuns([])
      return
    }
    try {
      const runs = await listActiveWorkflowRuns()
      setActiveRuns(runs.filter((run) => isWorkflowRunActive(run.status)))
    } catch {
      /* active runs refresh is best-effort */
    }
  }, [canRun])

  const refresh = useCallback(async (): Promise<boolean> => {
    setLoading(true)
    setError(null)
    try {
      const [mine, system] = await Promise.all([
        listWorkflowDefinitions('mine'),
        listWorkflowDefinitions('system'),
      ])
      const withPublicId = (items: WorkflowDefinition[]) =>
        items.filter((item) => isValidWorkflowDefinitionPublicId(workflowDefinitionPublicId(item)))
      setDefinitions(withPublicId(mine))
      setSystemTemplates(withPublicId(system).filter((item) => item.status !== 'archived'))
      await refreshActiveRuns()
      hasInitializedRef.current = true
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : copy.loadFailed
      setError(message)
      setDefinitions([])
      setSystemTemplates([])
      showApiErrorToast(showToast, err, copy.loadWorkflowsFailed, 'error')
      return false
    } finally {
      setLoading(false)
    }
  }, [refreshActiveRuns, showToast])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!canRun) return
    const trackedIds = new Set(activeRuns.map((run) => run.publicId))
    const unsubscribers = [...trackedIds].map((runId) =>
      workflowRunHub.subscribe(runId, (next) => {
        const normalized = normalizeWorkflowRun(next)
        setActiveRuns((prev) => {
          const exists = prev.some((item) => item.publicId === normalized.publicId)
          if (!isWorkflowRunActive(normalized.status)) {
            return prev.filter((item) => item.publicId !== normalized.publicId)
          }
          if (!exists) return [normalized, ...prev]
          return prev.map((item) => (item.publicId === normalized.publicId ? normalized : item))
        })
      }),
    )
    return () => {
      for (const unsubscribe of unsubscribers) unsubscribe()
    }
  }, [activeRuns.map((run) => run.publicId).join('\0'), canRun])

  useEffect(() => {
    if (!runTarget) return
    setRunInputValues(defaultInputValues(baseRunInputFields))
  }, [baseRunInputFields, runTarget])

  useEffect(() => {
    if (!runTarget) {
      setPreflightResult(null)
      return
    }
    const publicId = workflowDefinitionPublicId(runTarget)
    if (!isValidWorkflowDefinitionPublicId(publicId)) return
    let cancelled = false
    setPreflightLoading(true)
    const input = buildRunInputPayload(runInputFields, runInputValues)
    void preflightWorkflowDefinition({ definitionId: publicId, input })
      .then((result) => {
        if (!cancelled) setPreflightResult(result)
      })
      .catch(() => {
        if (!cancelled) {
          setPreflightResult(buildLocalWorkflowPreflight(runTarget.graph, runtimeStatus))
        }
      })
      .finally(() => {
        if (!cancelled) setPreflightLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [runInputFields, runInputValues, runTarget, runtimeStatus])

  useEffect(() => {
    if (!runTarget || !isComicProjectInitDefinition(runTarget) || !platformCatalogReady) return
    setRunInputValues((prev) => {
      const normalized = normalizeComicProjectInitInputValues(runInputFields, {
        ...defaultInputValues(runInputFields),
        ...prev,
      })
      const sameKeys = Object.keys(normalized).length === Object.keys(prev).length
      const unchanged = sameKeys && Object.entries(normalized).every(([key, value]) => prev[key] === value)
      return unchanged ? prev : normalized
    })
  }, [platformCatalogReady, runInputFields, runTarget])

  useEffect(() => {
    void getWorkflowRuntimeStatus()
      .then(setRuntimeStatus)
      .catch(() => setRuntimeStatus(null))
  }, [])

  useEffect(() => {
    const search = location.search.trim()
    if (!search || openedTemplateSearchRef.current === search || loading) return
    const params = new URLSearchParams(search)
    if (params.get('mode') !== 'run') return
    const templateId = params.get('template')?.trim() ?? ''
    if (!templateId) return
    const target = systemTemplates.find((item) => workflowDefinitionPublicId(item) === templateId)
    if (!target) return
    openedTemplateSearchRef.current = search
    setRunTarget(target)
    navigate('/workflows', { replace: true })
  }, [loading, location.search, navigate, systemTemplates])

  function openCreateTypeModal() {
    setCreateType('comic_story')
    setCreateName(WORKFLOW_TYPE_META.comic_story.defaultName)
    setCreateDescription('')
    setCreateTypeOpen(true)
  }

  function confirmCreateType() {
    const name = createName.trim()
    if (!name) {
      showToast({ message: copy.nameRequired, variant: 'warning' })
      return
    }
    setCreateTypeOpen(false)
    navigate(createBuilderUrl(createType, name, createDescription))
  }

  function openBuilder(definition: WorkflowDefinition) {
    const publicId = workflowDefinitionPublicId(definition)
    if (!isValidWorkflowDefinitionPublicId(publicId)) {
      showToast({ message: copy.invalidWorkflowLink, variant: 'warning' })
      return
    }
    navigate(`/workflows/builder/${encodeURIComponent(publicId)}`)
  }

  function quickSimulate(definition: WorkflowDefinition) {
    if (!canRun) {
      showToast({ message: copy.noRunPermission, variant: 'warning' })
      return
    }
    const publicId = workflowDefinitionPublicId(definition)
    if (!isValidWorkflowDefinitionPublicId(publicId)) {
      showToast({ message: copy.invalidWorkflowLink, variant: 'warning' })
      return
    }
    const display = resolveWorkflowDefinitionDisplay(definition)
    const fields = inferWorkflowInputFields(definition.graph)
    const input = buildRunInputPayload(fields, defaultInputValues(fields))
    setRunningDefinitionId(publicId)
    try {
      navigateToSimulatedWorkflowRun(navigate, {
        definitionId: publicId,
        definitionName: display.name,
        input,
        from: 'list',
      })
    } finally {
      setRunningDefinitionId(null)
    }
  }

  function openRunModal(definition: WorkflowDefinition) {
    if (!canRun) {
      showToast({ message: copy.noRunPermission, variant: 'warning' })
      return
    }
    setRunTarget(definition)
  }

  function openRunDetail(run: WorkflowRun) {
    if (!isValidWorkflowRunPublicId(run.publicId)) {
      showToast({ message: copy.invalidRunLink, variant: 'warning' })
      return
    }
    navigate(workflowRunPagePath(run.publicId))
  }

  async function handleRun() {
    if (!runTarget || !canRun) return
    const publicId = workflowDefinitionPublicId(runTarget)
    if (!isValidWorkflowDefinitionPublicId(publicId)) {
      showToast({ message: copy.invalidWorkflowLink, variant: 'warning' })
      return
    }
    const missingField = runInputFields.find((field) => field.required && !(runInputValues[field.key] ?? '').trim())
    if (missingField) {
      showToast({ message: `${copy.runMissingField}：${missingField.label}`, variant: 'warning' })
      return
    }
    if (preflightResult && !preflightResult.runnable) {
      showToast({ message: '工作流预检未通过，请先处理提示项', variant: 'warning' })
      return
    }
    setRunningDefinitionId(publicId)
    try {
      const input = buildRunInputPayload(runInputFields, runInputValues)
      const created = await createWorkflowRun({ definitionId: publicId, input })
      setRunTarget(null)
      navigate(`${workflowRunPagePath(created.publicId)}${buildWorkflowRunForwardSearch(location.search)}`)
      showToast({ message: '工作流已开始运行', variant: 'success', autoCloseMs: 2800 })
    } catch (err) {
      showApiErrorToast(showToast, err, '运行工作流失败', 'error')
    } finally {
      setRunningDefinitionId(null)
    }
  }

  async function handleCloneTemplate(definition: WorkflowDefinition) {
    const sourcePublicId = workflowDefinitionPublicId(definition)
    if (!isValidWorkflowDefinitionPublicId(sourcePublicId)) {
      showToast({ message: copy.invalidWorkflowLink, variant: 'warning' })
      return
    }
    setCloningDefinitionId(sourcePublicId)
    try {
      const cloned = await cloneWorkflowDefinition(sourcePublicId)
      showToast({ message: copy.clonedSuccess, variant: 'success' })
      await refresh()
      const clonedPublicId = workflowDefinitionPublicId(cloned)
      if (!isValidWorkflowDefinitionPublicId(clonedPublicId)) {
        showToast({ message: copy.invalidWorkflowLink, variant: 'warning' })
        return
      }
      navigate(`/workflows/builder/${encodeURIComponent(clonedPublicId)}`)
    } catch (err) {
      showApiErrorToast(showToast, err, copy.cloneFailed, 'error')
    } finally {
      setCloningDefinitionId(null)
    }
  }

  const shouldIgnoreWorkflowPullStart = useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(
      target.closest('button, input, textarea, select, [contenteditable="true"], [role="button"]'),
    )
  }, [])

  const onWorkflowPullRefreshCommit = useCallback(() => {
    if (workflowPullRefreshLockRef.current) return
    workflowPullRefreshLockRef.current = true
    void (async () => {
      try {
        const ok = await refresh()
        if (ok) showToast({ message: copy.listUpdated, variant: 'success', autoCloseMs: 4000 })
      } finally {
        workflowPullRefreshLockRef.current = false
      }
    })()
  }, [refresh, showToast])

  const createNameMissing = !createName.trim()
  const showInitialLoading = loading && definitions.length === 0 && systemTemplates.length === 0 && !error && !hasInitializedRef.current
  const showEmpty = !loading && !error && definitions.length === 0 && systemTemplates.length === 0

  const {
    pullPx: workflowPullPx,
    commitFlying: workflowPullCommitFlying,
    onCommitFlyComplete: onWorkflowPullCommitFlyComplete,
    releaseThreshold: workflowPullReleaseThreshold,
  } = useScrollPullToRefresh({
    scrollRef: workflowScrollRef,
    attachSignal: workflowPullAttachSignal,
    enabled: !showInitialLoading,
    refreshing: loading && !showInitialLoading,
    shouldIgnorePullStart: shouldIgnoreWorkflowPullStart,
    onCommit: onWorkflowPullRefreshCommit,
  })
  const workflowPullActive = workflowPullPx > 0 || workflowPullCommitFlying

  return (
    <ContentSkeleton
      title={copy.title}
      breadcrumbs={buildWorkflowsPageBreadcrumbs()}
      adminLayout
      framed={false}
      scrollableBody={false}
      shellScrollable={false}
      headerTrailing={
        <ChromeToolbarButton type="button" className="h-9 lg:hidden" onClick={openCreateTypeModal}>
          {copy.createWorkflow}
        </ChromeToolbarButton>
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden lg:h-full lg:min-h-0 lg:flex-row">
        <div
          ref={setWorkflowScrollPort}
          className="scroll-region flex min-h-0 min-w-0 flex-1 flex-col overflow-x-clip overflow-y-auto overscroll-y-contain px-4 pb-6 pt-2 sm:px-6 sm:pb-8 sm:pt-3 lg:min-h-0 lg:px-8 lg:pe-6"
        >
          <div
            className={[
              'will-change-transform flex min-h-0 min-w-0 flex-1 flex-col',
              workflowPullActive ? 'select-none' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            style={{ transform: `translate3d(0, ${workflowPullPx}px, 0)` }}
          >
            <div className="select-none" aria-live="polite">
              {(loading && !showInitialLoading) || workflowPullPx >= 8 || workflowPullCommitFlying ? (
                <TimelinePullIndicator
                  pullPx={workflowPullCommitFlying ? workflowPullReleaseThreshold : workflowPullPx}
                  releaseThreshold={workflowPullReleaseThreshold}
                  angular={angular}
                  commitFly={workflowPullCommitFlying}
                  onCommitFlyComplete={onWorkflowPullCommitFlyComplete}
                  refreshing={loading && !showInitialLoading}
                />
              ) : (
                <div className="h-0 min-h-px shrink-0" aria-hidden />
              )}
            </div>

            {showInitialLoading ? (
              <ThemedWorkflowState theme={theme} kind="loading" />
            ) : error ? (
              <ThemedWorkflowState theme={theme} kind="error" detail={error} onRetry={() => void refresh()} />
            ) : showEmpty ? (
              <ThemedWorkflowState theme={theme} kind="empty" onCreate={openCreateTypeModal} />
            ) : (
              <ThemeReveal>
                <WorkflowSystemTemplateSection
                  definitions={systemTemplates}
                  angular={angular}
                  busyDefinitionId={cloningDefinitionId ?? runningDefinitionId}
                  onRun={canRun ? openRunModal : undefined}
                  onOpenBuilder={openBuilder}
                  onQuickSimulate={canRun ? quickSimulate : undefined}
                  onClone={handleCloneTemplate}
                />

                {canRun ? (
                  <WorkflowActiveRunsPanel
                    angular={angular}
                    runs={activeRuns}
                    definitionNames={definitionNames}
                    onOpenRun={openRunDetail}
                  />
                ) : null}

                {definitions.length > 0 ? (
                  <section>
                    <div className="mb-3">
                      <h2 className="text-sm font-bold text-ink">{copy.myWorkflows}</h2>
                    </div>
                    <WorkflowShelf
                      definitions={definitions}
                      angular={angular}
                      layoutReflow={!workflowPullActive}
                      runningDefinitionId={runningDefinitionId}
                      onRun={canRun ? openRunModal : undefined}
                    />
                  </section>
                ) : systemTemplates.length > 0 ? (
                  <p className="text-sm text-ink-soft">{copy.noSavedWorkflows}</p>
                ) : null}
              </ThemeReveal>
            )}
          </div>
        </div>

        <aside
          className={[
            'shell-rail hidden h-full min-h-0 w-16 shrink-0 flex-col items-center border-line bg-panel/70 lg:flex',
            angular ? 'border-l-2' : 'border-l',
          ].join(' ')}
        >
          <div className="scroll-region flex min-h-0 flex-1 flex-col items-center gap-3 overflow-y-auto p-2">
            <ChromeTooltip content={copy.createWorkflow} angular={angular} side="top" align="center">
              <span className="inline-flex">
                <ChromePrimaryButton
                  type="button"
                  density="compact"
                  className="h-10 w-10 !p-0"
                  onClick={openCreateTypeModal}
                  aria-label={copy.createWorkflow}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M12 5v14M5 12h14" /></svg>
                </ChromePrimaryButton>
              </span>
            </ChromeTooltip>
          </div>
        </aside>
      </div>

      <ThemeModal
        open={createTypeOpen}
        onClose={() => setCreateTypeOpen(false)}
        title={copy.createWorkflow}
        angular={angular}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setCreateTypeOpen(false)}>
              {copy.cancel}
            </ChromeToolbarButton>
            <ChromePrimaryButton density="compact" type="button" onClick={confirmCreateType} disabled={createNameMissing}>
              {copy.enterBuilder}
            </ChromePrimaryButton>
          </div>
        }
      >
        <div className="space-y-4">
          <WorkflowFormField label={copy.nameLabel}>
            <ChromeTextInput
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              maxLength={128}
              autoFocus
            />
          </WorkflowFormField>
          <WorkflowFormField label={copy.typeLabel}>
            <SelectMenu
              ariaLabel={copy.workflowTypeAria}
              value={createType}
              onChange={(next) => {
                const type = next as WorkflowType
                setCreateType(type)
                setCreateName(WORKFLOW_TYPE_META[type].defaultName)
              }}
              options={workflowTypeOptions()}
              angular={angular}
              density="compact"
            />
          </WorkflowFormField>
          <WorkflowFormField label={copy.remarkLabel}>
            <ChromeTextInput
              value={createDescription}
              onChange={(event) => setCreateDescription(event.target.value)}
              maxLength={256}
              placeholder={copy.remarkPlaceholder}
            />
          </WorkflowFormField>
        </div>
      </ThemeModal>

      <ThemeModal
        open={runTarget != null}
        onClose={() => {
          if (!runningDefinitionId) setRunTarget(null)
        }}
        title={runTarget ? `${copy.runWorkflow} · ${resolveWorkflowDefinitionDisplay(runTarget).name}` : copy.runWorkflow}
        angular={angular}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <ChromeToolbarButton type="button" onClick={() => setRunTarget(null)} disabled={Boolean(runningDefinitionId)}>
              {copy.cancel}
            </ChromeToolbarButton>
            <ChromePrimaryButton
              density="compact"
              type="button"
              onClick={() => void handleRun()}
              disabled={Boolean(runningDefinitionId)}
            >
              {runningDefinitionId ? copy.navigating : copy.startRun}
            </ChromePrimaryButton>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-soft">{copy.runHint}</p>
          {preflightLoading ? (
            <p className="text-sm text-ink-soft">{copy.preflightChecking}</p>
          ) : (
            <WorkflowPreflightPanel
              angular={angular}
              issues={preflightResult?.issues ?? []}
              runtime={preflightResult?.runtime ?? runtimeStatus}
            />
          )}
          <WorkflowRunInputFields
            angular={angular}
            fields={runInputFields}
            values={runInputValues}
            disabled={Boolean(runningDefinitionId)}
            onChange={(key, value) => setRunInputValues((prev) => ({ ...prev, [key]: value }))}
          />
        </div>
      </ThemeModal>
    </ContentSkeleton>
  )
}
